import React, {
    FC,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import Measure, { MeasureProps } from 'react-measure';

import { animation, cn } from 'Modules/ui/utils';

import { ClosingOverlay } from '../ClosingOverlay';
import { Portal } from '../Portal';
import { useSizeContext } from '../SizeContext';
import { TransitionComponent } from '../TransitionComponent';
import styles from './index.module.scss';


export type PopupProps = {
    isOpen: boolean;
    offset?: number;
    closingDelay?: number;
    parentPosition?: DOMRect | { bottom, top, left, height, right };
    onClose?: () => void;
    overlayZIndex?: number;
    fullWidth?: boolean;
}

type PopupPosition = {
    top: string;
    bottom: string;
    left: string;
    width: string;
}

const useBlurDelay = ({ closingDelay, isOpen }) => {
    const [isDelayedOpen, setDelayedOpen] = useState(isOpen);

    const blurDelayTimer = useRef<number>();
    const startHiding = useCallback(
        () => {
            const fn: TimerHandler = () => {
                setDelayedOpen(false);
            };
            blurDelayTimer.current = setTimeout(fn, closingDelay);
        },
        [closingDelay],
    );

    useLayoutEffect(() => {
        if (closingDelay && !isOpen && isDelayedOpen)
            startHiding();
        if (closingDelay && isOpen && !isDelayedOpen) {
            clearTimeout(blurDelayTimer.current);
            setDelayedOpen(true);
        }

        return () => clearTimeout(blurDelayTimer.current);
    }, [closingDelay, isDelayedOpen, isOpen, startHiding]);

    const clearBlurDelay = useCallback(
        () => clearTimeout(blurDelayTimer.current),
        [],
    );

    return {
        isDelayedOpen,
        clearBlurDelay,
    };
};

export const Popup: FC<PopupProps> = ({
    isOpen,
    offset = 0,
    closingDelay,
    parentPosition,
    onClose,
    children,
    overlayZIndex,
    fullWidth,
}) => {
    const [position, setPosition] = useState<PopupPosition>(null);

    const { viewportHeight } = useSizeContext();
    const popupHeight = useRef<number>();
    const calculatePosition = useCallback(
        () => {
            if (!popupHeight.current || !parentPosition)
                return;

            const { bottom, top, left, height, right } = parentPosition;
            const fitsBelow = bottom + popupHeight.current < window.innerHeight;
            const estimatedTop = top - popupHeight.current;
            const maxBottom = window.innerHeight - popupHeight.current;
            const estimatedBottom = window.innerHeight - bottom + height;
            setPosition({
                left: `${left}px`,
                top: fitsBelow
                    ? `${top + height + offset}px`
                    : estimatedTop < 0 ? '0px' : 'auto',
                bottom: fitsBelow || maxBottom < estimatedBottom
                    ? 'auto'
                    : `${estimatedBottom + offset}px`,
                width: fullWidth ? `${right - left}px` : 'unset',
            });
        },
        [fullWidth, offset, parentPosition],
    );
    const setCalendarHeight = useCallback<MeasureProps['onResize']>(
        ({ bounds: { height } }) => {
            if (popupHeight.current !== height) {
                popupHeight.current = height;
                calculatePosition();
            }
        },
        [calculatePosition],
    );
    useEffect(() => calculatePosition(), [calculatePosition, viewportHeight]);

    const { clearBlurDelay, isDelayedOpen } = useBlurDelay({ closingDelay, isOpen });

    return (
        <Portal>
            <TransitionComponent in={closingDelay ? isDelayedOpen : isOpen} timeout={animation}>
                <div>
                    <Measure bounds onResize={setCalendarHeight}>
                        {({ measureRef }) => (
                            <div
                                ref={measureRef}
                                className={cn(styles.popup)}
                                style={position}
                                onFocus={clearBlurDelay}
                            >
                                {children}
                            </div>
                        )}
                    </Measure>
                    {onClose && (
                        <ClosingOverlay
                            zIndex={overlayZIndex}
                            onClose={onClose}
                            closeOnWheel
                        />
                    )}
                </div>
            </TransitionComponent>
        </Portal>
    );
};
